/**
 * AppStartPage — `/app` and `/app/start`. Public, no shell, no auth.
 *
 * The front door for the free personal Monitor app. Pain-led hero,
 * one-tap "Get started" which creates a personal account (auto-named),
 * stores the token, and drops the user straight into the Privacy screen.
 *
 * If a token already exists, it skips straight to Privacy.
 */

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ShieldCheck, Wallet, Bell } from 'lucide-react';

import {
  signup,
  saveMonitorSession,
  getMonitorToken,
  MonitorError,
} from '@/pages/monitor/monitorClient';
import { c, font } from './theme';

function autoName(): string {
  // Friendly, personal default name for the account.
  return 'My Monitor';
}

export function AppStartPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');
  const [error, setError] = useState('');

  if (getMonitorToken()) return <Navigate to="/app/privacy" replace />;

  async function getStarted() {
    setStatus('working');
    setError('');
    try {
      const res = await signup(autoName());
      saveMonitorSession({
        token: res.api_token,
        ingestUrl: res.ingest_url,
        workspace: autoName(),
      });
      navigate('/app/privacy', { replace: true });
    } catch (err) {
      setError(
        err instanceof MonitorError
          ? err.message
          : "Couldn't set you up just now. Check your connection and try again.",
      );
      setStatus('error');
    }
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: c.surface,
        color: c.ink,
        fontFamily: font,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          maxWidth: 480,
          width: '100%',
          margin: '0 auto',
          padding: '32px 24px 40px',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: c.accent,
          }}
        >
          Monitor
        </div>

        <h1 style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.08, margin: '14px 0 12px' }}>
          See who's tracking you.
          <br />
          <span style={{ color: c.accent }}>Free.</span>
        </h1>
        <p style={{ fontSize: 17, color: c.inkMuted, lineHeight: 1.5, margin: '0 0 28px' }}>
          Your apps watch your mic, your location, your every tap. Monitor shows you who — and
          helps you shut it down, save money, and never forget the things that matter.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
          <Bullet Icon={ShieldCheck} text="See every app spying on you — and block them" />
          <Bullet Icon={Wallet} text="Find money leaking out of your account" />
          <Bullet Icon={Bell} text="Little nudges so you never drop the ball" />
        </div>

        {status === 'error' && (
          <p style={{ fontSize: 14, color: c.danger, margin: '0 0 14px' }}>{error}</p>
        )}

        <button
          type="button"
          onClick={getStarted}
          disabled={status === 'working'}
          style={{
            width: '100%',
            minHeight: 54,
            fontSize: 18,
            fontWeight: 800,
            color: '#ffffff',
            background: c.accent,
            border: 'none',
            borderRadius: 14,
            cursor: status === 'working' ? 'default' : 'pointer',
          }}
        >
          {status === 'working' ? 'Setting up…' : 'Get started — free'}
        </button>
        <p style={{ fontSize: 13, color: c.inkSubtle, textAlign: 'center', margin: '14px 0 0' }}>
          No sign-up forms. No credit card. Takes one tap.
        </p>
      </main>
    </div>
  );
}

function Bullet({ Icon, text }: { Icon: typeof ShieldCheck; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 12,
          background: c.accentSoft,
          color: c.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} />
      </span>
      <span style={{ fontSize: 15, fontWeight: 600, color: c.ink }}>{text}</span>
    </div>
  );
}

export default AppStartPage;
